import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, WarningCircle, XCircle, ArrowClockwise,
  CaretRight, CaretDown, Users, Info, Receipt, Envelope,
} from "@phosphor-icons/react";
import client from "../lib/api";
import BrokerCredentialsCard from "../components/BrokerCredentialsCard";

const money = (n) =>
  "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const BAND_META = {
  green: {
    label: "Low risk",
    icon: ShieldCheck,
    tone: "text-emerald-300",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/30",
    dot: "bg-emerald-400",
  },
  amber: {
    label: "Watch",
    icon: WarningCircle,
    tone: "text-amber-300",
    bg: "bg-amber-500/10",
    border: "border-amber-500/30",
    dot: "bg-amber-400",
  },
  red: {
    label: "High risk",
    icon: XCircle,
    tone: "text-rose-300",
    bg: "bg-rose-500/10",
    border: "border-rose-500/40",
    dot: "bg-rose-400",
  },
};

/**
 * Customer Credit Score — per-customer scorecards showing which brokers/shippers
 * pay on time vs. chronically late. Uses aggregated invoice + reminder history.
 * Sorted red → amber → green so risky customers surface first.
 */
export default function CustomerCreditPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/customers/credit-scores");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load credit scores");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (name) => {
    if (expandedCustomer === name) {
      setExpandedCustomer(null);
      setDetail(null);
      return;
    }
    setExpandedCustomer(name);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await client.get(
        `/customers/credit-scores/${encodeURIComponent(name)}`,
      );
      setDetail(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load customer detail");
    } finally { setDetailLoading(false); }
  };

  const t = data?.totals || {};
  const customers = data?.customers || [];

  return (
    <div className="p-4 sm:p-8 max-w-6xl" data-testid="customer-credit-page">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-1">
            <Users size={12} weight="fill" /> Credit intelligence
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            Customer Credit Scores
          </h1>
          <p className="text-sm text-neutral-400 mt-2 max-w-2xl">
            Risk-band every customer based on their payment history — average days-to-pay,
            outstanding balances, and reminder count. Flag risky brokers <em>before</em> booking
            the next load.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="customer-credit-refresh-btn"
          className="text-xs rounded-full px-3 py-1.5 border border-white/10 text-neutral-300 hover:border-white/20 inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          <ArrowClockwise size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Broker credentials verification card — sits above the credit table
          so pilots see BOTH sides of a broker: "authorized?" + "pays on time?" */}
      <BrokerCredentialsCard />

      {/* Summary bar */}
      <div
        className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6"
        data-testid="customer-credit-summary"
      >
        {["red", "amber", "green"].map((band) => {
          const meta = BAND_META[band];
          const Icon = meta.icon;
          return (
            <div
              key={band}
              data-testid={`customer-credit-band-${band}`}
              className={`card-surface p-4 border-l-2 ${meta.border.replace("border-", "border-l-")}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon size={14} className={meta.tone} weight="fill" />
                <span className={`text-[10px] uppercase tracking-widest font-semibold ${meta.tone}`}>
                  {meta.label}
                </span>
              </div>
              <div className="text-3xl font-semibold text-white">{t[band] || 0}</div>
              <div className="text-[11px] text-neutral-500 mt-1">customers</div>
            </div>
          );
        })}
        <div className="card-surface p-4 border-l-2 border-l-[#FF5A1F]/40" data-testid="customer-credit-outstanding-total">
          <div className="flex items-center gap-2 mb-1">
            <Receipt size={14} className="text-[#FF9764]" weight="fill" />
            <span className="text-[10px] uppercase tracking-widest font-semibold text-[#FF9764]">
              Outstanding
            </span>
          </div>
          <div className="text-2xl font-semibold text-white font-mono">
            {money(t.total_outstanding_across_all)}
          </div>
          <div className="text-[11px] text-neutral-500 mt-1">across all customers</div>
        </div>
      </div>

      {/* Customer table */}
      {loading && !data ? (
        <div className="card-surface p-8 text-center text-neutral-500 italic">
          Loading credit scores...
        </div>
      ) : customers.length === 0 ? (
        <div className="card-surface p-8 text-center" data-testid="customer-credit-empty">
          <Users size={32} className="mx-auto text-neutral-600 mb-2" />
          <div className="text-white font-medium">No customers yet</div>
          <div className="text-sm text-neutral-500 mt-1">
            Create some invoices and their customers will appear here with credit scores.
          </div>
        </div>
      ) : (
        <div className="card-surface overflow-hidden" data-testid="customer-credit-table">
          {/* Header row */}
          <div className="grid grid-cols-[24px_2fr_1fr_1fr_1.5fr_100px] gap-3 px-4 py-3 text-[10px] uppercase tracking-widest text-neutral-500 border-b border-white/5 font-semibold">
            <div></div>
            <div>Customer</div>
            <div className="text-right">Outstanding</div>
            <div className="text-right">Total billed</div>
            <div>Score reasons</div>
            <div className="text-right">Band</div>
          </div>
          {customers.map((c) => {
            const meta = BAND_META[c.band];
            const isOpen = expandedCustomer === c.customer;
            return (
              <div key={c.customer} data-testid={`customer-row-${c.customer}`}>
                <button
                  onClick={() => openDetail(c.customer)}
                  className={`w-full grid grid-cols-[24px_2fr_1fr_1fr_1.5fr_100px] gap-3 px-4 py-3 text-left border-t border-white/5 hover:bg-white/[0.02] transition-colors ${
                    isOpen ? "bg-white/[0.02]" : ""
                  }`}
                >
                  <div className="flex items-center">
                    {isOpen ? (
                      <CaretDown size={12} className="text-neutral-500" weight="bold" />
                    ) : (
                      <CaretRight size={12} className="text-neutral-500" weight="bold" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${meta.dot}`} />
                    <div className="min-w-0">
                      <div className="text-white text-sm font-medium truncate">{c.customer}</div>
                      <div className="text-[11px] text-neutral-500 truncate">
                        {c.invoice_count} invoice{c.invoice_count !== 1 ? "s" : ""}
                        {c.avg_days_to_pay !== null && ` · ${c.avg_days_to_pay < 0 ? Math.abs(c.avg_days_to_pay) + "d early" : c.avg_days_to_pay + "d late"} avg`}
                      </div>
                    </div>
                  </div>
                  <div className="text-right font-mono text-sm text-white flex items-center justify-end">
                    {c.total_outstanding > 0 ? money(c.total_outstanding) : (
                      <span className="text-neutral-600">—</span>
                    )}
                  </div>
                  <div className="text-right font-mono text-sm text-neutral-300 flex items-center justify-end">
                    {money(c.total_billed)}
                  </div>
                  <div className="text-[11px] text-neutral-400 flex items-center min-w-0">
                    <span className="truncate">{c.reasons?.join(" · ") || "—"}</span>
                  </div>
                  <div className="text-right flex items-center justify-end">
                    <span
                      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold rounded-full px-2 py-0.5 border ${meta.tone} ${meta.bg} ${meta.border}`}
                    >
                      {meta.label}
                    </span>
                  </div>
                </button>

                {/* Expanded detail */}
                {isOpen && (
                  <div
                    className="border-t border-white/5 bg-black/40 p-4"
                    data-testid={`customer-detail-${c.customer}`}
                  >
                    {detailLoading || !detail ? (
                      <div className="text-neutral-500 italic text-sm">Loading detail...</div>
                    ) : (
                      <>
                        {/* Score breakdown */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                          <StatCell label="Paid on time" value={`${detail.invoice_count_paid}/${detail.invoice_count}`} />
                          <StatCell label="Avg days-to-pay" value={
                            detail.avg_days_to_pay === null ? "—" :
                            detail.avg_days_to_pay < 0 ? `${Math.abs(detail.avg_days_to_pay)}d early` :
                            `${detail.avg_days_to_pay}d late`
                          } />
                          <StatCell label="Oldest overdue" value={
                            detail.oldest_outstanding_days > 0 ? `${detail.oldest_outstanding_days}d` : "None"
                          } />
                          <StatCell label="Reminders sent" value={detail.reminder_count} icon={Envelope} />
                        </div>

                        {/* Invoice history */}
                        <div className="mb-3 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
                          Invoice history ({detail.invoices.length})
                        </div>
                        <div className="space-y-1 mb-4">
                          {detail.invoices.slice(0, 10).map((inv) => (
                            <div
                              key={inv.id}
                              className="grid grid-cols-[80px_60px_1fr_100px_60px] gap-2 items-center text-[11px] px-3 py-1.5 rounded bg-white/[0.02]"
                            >
                              <span className="font-mono text-neutral-300 truncate">#{inv.invoice_number}</span>
                              <span className="text-neutral-500">{inv.due_date}</span>
                              <span className="text-neutral-600 truncate">{inv.load_reference || "—"}</span>
                              <span className="text-right font-mono text-white">{money(inv.amount)}</span>
                              <span className={`text-right text-[10px] uppercase font-semibold ${
                                inv.status === "paid" ? "text-emerald-400" :
                                inv.status === "overdue" ? "text-rose-400" :
                                inv.status === "sent" ? "text-amber-400" :
                                "text-neutral-500"
                              }`}>{inv.status}</span>
                            </div>
                          ))}
                          {detail.invoices.length > 10 && (
                            <div className="text-[11px] text-neutral-600 italic pl-3">
                              + {detail.invoices.length - 10} more
                            </div>
                          )}
                        </div>

                        {/* Reminder history */}
                        {detail.reminders.length > 0 && (
                          <>
                            <div className="mb-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
                              Reminders sent ({detail.reminders.length})
                            </div>
                            <div className="space-y-1">
                              {detail.reminders.slice(0, 5).map((r) => (
                                <div key={r.id} className="text-[11px] px-3 py-1 rounded bg-amber-500/[0.06] flex items-center gap-2">
                                  <span className="text-amber-300 font-semibold uppercase text-[10px]">
                                    {r.stage}-day
                                  </span>
                                  <span className="text-neutral-400 truncate flex-1">{r.subject}</span>
                                  <span className="text-neutral-600 font-mono">{(r.sent_at || "").slice(0, 10)}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6 rounded-lg border border-white/10 bg-white/[0.02] p-3 text-[11px] text-neutral-400 flex items-start gap-2">
        <Info size={12} className="flex-shrink-0 mt-0.5" />
        <span>
          <b className="text-neutral-200">How scores are calculated:</b> Green = pays on time, no
          reminders, nothing overdue &gt;30d. Amber = slow-but-pays (avg 15-45d late), OR outstanding
          30-60d, OR at least one reminder sent. Red = chronic late (avg &gt;45d late), OR outstanding
          &gt;60d, OR received a 60-day or FINAL notice. Customers with the same name across invoices
          are grouped together.
        </span>
      </div>
    </div>
  );
}

function StatCell({ label, value, icon: Icon }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={11} className="text-neutral-500" />}
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
          {label}
        </div>
      </div>
      <div className="text-white text-lg font-semibold">{value}</div>
    </div>
  );
}
